import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  Children,
} from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  styled,
} from "@mui/material";

// --- Public API ---

export interface CarouselDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Dialog title text */
  title: string;
  /** Card elements to display in the carousel */
  children: ReactNode;
  /** Number of cards visible at once (default 3) */
  visibleCount?: number;
}

// --- Icons ---

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9"
      stroke="rgba(255,255,255,0.7)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Constants ---

const CARD_WIDTH = 278;
const CARD_GAP = 16;
const ANIMATION_MS = 300;
const SWIPE_THRESHOLD = 50;

// --- Styled components ---

const StyledDialog = styled(Dialog, {
  label: "CarouselDialog",
})(() => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    backgroundColor: "#1c252e",
    maxWidth: "none",
    width: "fit-content",
    boxShadow:
      "0px 0px 2px 0px rgba(145, 158, 171, 0.24), -20px 20px 40px -4px rgba(145, 158, 171, 0.24)",
    overflow: "hidden",
  },
}));

const CarouselContainer = styled(Box, {
  label: "CarouselContainer",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 24,
  padding: "0 24px",
}));

const NavButton = styled(IconButton, {
  label: "CarouselNav",
})<{ disabled?: boolean }>(({ disabled }) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: disabled ? "#919eab" : "#f4f6f8",
  color: disabled ? "#ffffff" : "#1c252e",
  opacity: disabled ? 0.65 : 1,
  flexShrink: 0,
  "&:hover": {
    backgroundColor: disabled ? "#919eab" : "#dfe3e8",
  },
  "&.Mui-disabled": {
    backgroundColor: "#919eab",
    color: "#ffffff",
    opacity: 0.65,
  },
}));

// --- Carousel component ---

export const CarouselDialog = ({
  open,
  onClose,
  title,
  children,
  visibleCount = 3,
}: CarouselDialogProps) => {
  const items = Children.toArray(children);
  const itemCount = items.length;
  const [scrollIndex, setScrollIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  const maxIndex = Math.max(0, itemCount - visibleCount);

  useEffect(() => {
    if (open) setScrollIndex(0);
  }, [open]);

  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex < maxIndex;
  const showNav = itemCount > visibleCount;

  const trackWidth = visibleCount * CARD_WIDTH + (visibleCount - 1) * CARD_GAP;

  const navigate = useCallback(
    (direction: "left" | "right") => {
      if (isAnimating) return;
      const canGo =
        direction === "left" ? scrollIndex > 0 : scrollIndex < maxIndex;
      if (!canGo) return;

      setIsAnimating(true);
      setScrollIndex((prev) =>
        direction === "left"
          ? Math.max(0, prev - 1)
          : Math.min(maxIndex, prev + 1),
      );
      setTimeout(() => setIsAnimating(false), ANIMATION_MS + 50);
    },
    [isAnimating, scrollIndex, maxIndex],
  );

  // Keyboard navigation (left/right arrows)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigate("left");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigate("right");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, navigate]);

  // Mouse wheel (horizontal scroll or vertical scroll on the carousel area)
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      // Use horizontal delta if available, otherwise vertical
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;
      e.preventDefault();
      if (delta > 0) {
        navigate("right");
      } else {
        navigate("left");
      }
    },
    [navigate],
  );

  // Touch swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (Math.abs(touchDeltaX.current) > SWIPE_THRESHOLD) {
      if (touchDeltaX.current > 0) {
        navigate("left");
      } else {
        navigate("right");
      }
    }
    touchDeltaX.current = 0;
  }, [navigate]);

  const translateX = -(scrollIndex * (CARD_WIDTH + CARD_GAP));

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pl: 3,
          pr: 1.5,
          py: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Proxima Nova, sans-serif",
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "36px",
            color: "#ffffff",
            flex: 1,
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <CarouselContainer
        ref={containerRef}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {showNav && (
          <NavButton
            disabled={!canScrollLeft || isAnimating}
            onClick={() => navigate("left")}
          >
            <ArrowLeftIcon />
          </NavButton>
        )}
        <Box
          sx={{
            width: trackWidth,
            overflow: "hidden",
            py: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: `${CARD_GAP}px`,
              transform: `translateX(${translateX}px)`,
              transition: `transform ${ANIMATION_MS}ms ease-in-out`,
            }}
          >
            {items.map((item, index) => {
              const isVisible =
                index >= scrollIndex && index < scrollIndex + visibleCount;

              return (
                <Box
                  key={index}
                  sx={{
                    flexShrink: 0,
                    opacity: isVisible ? 1 : 0,
                    transition: `opacity ${ANIMATION_MS}ms ease-in-out`,
                  }}
                >
                  {item}
                </Box>
              );
            })}
          </Box>
        </Box>
        {showNav && (
          <NavButton
            disabled={!canScrollRight || isAnimating}
            onClick={() => navigate("right")}
          >
            <ArrowRightIcon />
          </NavButton>
        )}
      </CarouselContainer>
    </StyledDialog>
  );
};

// --- Shared card styled components ---

export const CAROUSEL_CARD_OVERLAY_CLASS = "CarouselCardOverlay-root";

export const CarouselCard = styled(Box, {
  label: "CarouselCard",
})(() => ({
  width: CARD_WIDTH,
  height: CARD_WIDTH,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  flexShrink: 0,
  cursor: "pointer",
  [`& .${CAROUSEL_CARD_OVERLAY_CLASS}`]: {
    transition: "background-color 200ms ease-in-out",
  },
  [`&:hover .${CAROUSEL_CARD_OVERLAY_CLASS}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.25)",
  },
}));

export const CarouselCardImage = styled("img", {
  label: "CarouselCardImage",
})(() => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  pointerEvents: "none",
  borderRadius: 20,
}));

export const CarouselCardOverlay = styled(Box, {
  label: "CarouselCardOverlay",
})(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  borderRadius: 20,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  gap: 21,
  padding: 24,
}));

CarouselCardOverlay.defaultProps = {
  className: CAROUSEL_CARD_OVERLAY_CLASS,
};

export const CarouselCardName = styled(Typography, {
  label: "CarouselCardName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "30px",
  color: "#ffffff",
}));
